import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, ShieldCheck, Upload, Landmark, X, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { ModuleGuide } from "./ModuleGuide";
import { StepBar, Primary, Footer, eur } from "./ui";

const LABELS = ["Connexion", "Compte des loyers", "Premier rapprochement"];

export const Reassurance = () => (
  <div className="rounded-2xl bg-brand-softer p-4 text-sm text-brand-greenDeep space-y-1" data-testid="bank-reassurance">
    <p className="flex items-center gap-2 font-semibold"><ShieldCheck className="w-4 h-4 text-brand-limeDark" /> Accès en lecture seule, en toute sécurité</p>
    <p className="text-brand-green/70">Agrégateur agréé DSP2 (Powens). Aucun virement possible depuis l'application. Vous pouvez vous déconnecter à tout moment.</p>
  </div>
);

const Connect = ({ ov, onImported }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const connect = async () => { try { const r = await api.post("/powens/connect/start"); window.location.assign(r.data.url); } catch (e) { toast.error(e?.response?.data?.detail || "Connexion indisponible"); } };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/bank/import", fd); toast.success("Relevé importé"); onImported(); } catch (e) { toast.error(e?.response?.data?.detail || "Fichier illisible"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="bank-connect">
      <Reassurance />
      {ov.accounts.length > 0 && <p className="text-sm text-brand-green/70" data-testid="bank-has-accounts">{ov.accounts.length} compte(s) déjà connecté(s) ou importé(s).</p>}
      <div className="grid sm:grid-cols-2 gap-3">
        <button disabled={!ov.powens_ready} onClick={connect} className="rounded-2xl border border-brand-soft p-4 text-left hover:bg-brand-softer disabled:opacity-50" data-testid="bank-connect-powens">
          <Landmark className="w-6 h-6 text-brand-limeDark" /><p className="font-semibold mt-2">Connecter ma banque</p>
          <p className="text-xs text-brand-green/60">{ov.powens_ready ? "Synchronisation automatique" : "Bientôt disponible"}</p>
        </button>
        <button onClick={() => ref.current.click()} className="rounded-2xl border border-brand-soft p-4 text-left hover:bg-brand-softer" data-testid="bank-import-btn">
          <Upload className="w-6 h-6 text-brand-limeDark" /><p className="font-semibold mt-2">{busy ? "Import…" : "Importer un relevé"}</p>
          <p className="text-xs text-brand-green/60">Fichier OFX ou QIF exporté de votre banque</p>
        </button>
        <input ref={ref} type="file" accept=".ofx,.qif" hidden onChange={(e) => upload(e.target.files[0])} data-testid="bank-import-input" />
      </div>
    </div>
  );
};

const Matches = ({ data, onValidate, onReject }) => (
  <div data-testid="bank-matches">
    {data.suggestions.length === 0 ? <p className="text-sm text-brand-green/60" data-testid="bank-no-match">Aucun loyer correspondant sur les 3 derniers mois. Les prochains seront rapprochés automatiquement.</p> : (
      <ul className="space-y-2">
        {data.suggestions.map((s) => (
          <li key={s.bt.id} className="rounded-2xl border border-brand-soft p-3 text-sm" data-testid={`bank-match-${s.bt.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex-1 min-w-[180px]"><b>{Number(s.bt.amount).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</b> · {s.bt.date} · <span className="text-brand-green/60">{s.bt.label}</span></span>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-brand-softer">{s.confidence}</span>
            </div>
            <p className="text-xs text-brand-green/70 mt-1">→ Loyer {s.receipt.month} · {s.receipt.tenant} · {s.receipt.property}</p>
            <div className="flex gap-2 mt-2">
              <button onClick={() => onValidate([s])} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-brand-lime text-xs font-semibold" data-testid={`bank-validate-${s.bt.id}`}><Check className="w-3 h-3" /> Valider</button>
              <button onClick={() => onReject(s)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-brand-softer text-xs" data-testid={`bank-reject-${s.bt.id}`}><X className="w-3 h-3" /> Ce n'est pas ça</button>
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const Steps = ({ onDone }) => {
  const [ov, setOv] = useState(null);
  const [step, setStep] = useState(1);
  const [acc, setAcc] = useState([]);
  const [data, setData] = useState(null);
  const [count, setCount] = useState(0);
  const loadOv = () => api.get("/guides/bank/overview").then((r) => { setOv(r.data); setAcc(r.data.accounts.map((a) => a.id)); if (r.data.accounts.length && step === 1 && new URLSearchParams(window.location.search).get("powens")) setStep(2); });
  useEffect(() => { loadOv(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const loadMatches = () => api.get("/guides/bank/suggestions", { params: { account_ids: acc.join(",") } }).then((r) => { setData(r.data); setStep(3); });
  const validate = async (list) => {
    const r = await api.post("/guides/bank/validate", { pairs: list.map((s) => ({ bt_id: s.bt.id, receipt_id: s.receipt.id })) });
    if (r.data.failed) toast.error(`${r.data.failed} rapprochement(s) n'ont pas pu être enregistrés : faites-les depuis la liste des opérations.`);
    setCount((c) => c + r.data.reconciled);
    setData((d) => ({ ...d, suggestions: d.suggestions.filter((s) => !list.includes(s)) }));
  };
  const reject = async (s) => { await api.post("/guides/bank/reject", { bt_id: s.bt.id, receipt_id: s.receipt.id }); setData((d) => ({ ...d, suggestions: d.suggestions.filter((x) => x !== s) })); };
  if (!ov) return null;
  if (step === 4) return (
    <div className="pb-6" data-testid="bank-success">
      <CheckCircle2 className="w-10 h-10 text-brand-limeDark" />
      <h3 className="font-display text-2xl text-brand-greenDeep mt-3" data-testid="bank-success-count">{count} loyer(s) rapproché(s). Les prochains seront automatiques.</h3>
      <p className="text-sm text-brand-green/70 mt-2">{data?.unreconciled || 0} opération(s) restent à classer : vous pouvez les transformer en dépenses depuis la liste du rapprochement.</p>
      <Footer><Primary onClick={onDone} data-testid="bank-done">Voir les opérations non rapprochées</Primary></Footer>
    </div>
  );
  return (
    <div data-testid="bank-steps">
      <StepBar step={step} total={3} labels={LABELS} />
      {step === 1 && <Connect ov={ov} onImported={() => { loadOv(); setStep(2); }} />}
      {step === 2 && (
        <div className="space-y-2" data-testid="bank-accounts">
          <p className="text-sm text-brand-green/70 mb-2">Sur quel(s) compte(s) recevez-vous vos loyers ?</p>
          {ov.accounts.map((a) => (
            <label key={a.id} className="flex items-center gap-3 rounded-2xl border border-brand-soft px-4 py-3 text-sm">
              <input type="checkbox" checked={acc.includes(a.id)} onChange={(e) => setAcc(e.target.checked ? [...acc, a.id] : acc.filter((x) => x !== a.id))} data-testid={`bank-account-${a.id}`} />
              <span className="flex-1">{a.name || a.bank || "Compte"}</span><span className="text-brand-green/60">{eur(a.balance)}</span>
            </label>
          ))}
        </div>
      )}
      {step === 3 && data && <Matches data={data} onValidate={validate} onReject={reject} />}
      <Footer onBack={step > 1 ? () => setStep(step - 1) : null}>
        {step === 1 && ov.accounts.length > 0 && <Primary onClick={() => setStep(2)} data-testid="bank-next-1">Continuer</Primary>}
        {step === 2 && <Primary onClick={loadMatches} disabled={!acc.length} data-testid="bank-next-2">Chercher mes loyers</Primary>}
        {step === 3 && data?.suggestions.length > 0 && <button onClick={() => validate(data.suggestions)} className="px-4 py-3 rounded-full border border-brand-soft text-sm font-semibold" data-testid="bank-validate-all">Tout valider</button>}
        {step === 3 && <Primary onClick={() => setStep(4)} data-testid="bank-next-3">Terminer</Primary>}
      </Footer>
    </div>
  );
};

export const BankGuide = () => (
  <ModuleGuide module="bank" label="Guide Rapprochement" intro="Fini le pointage à la main : vos loyers sont marqués payés automatiquement."
    benefit="Connectez votre banque ou importez un relevé, validez vos premiers loyers en un clic." preview={<Reassurance />} Steps={Steps} />
);

export const ConsentBanner = () => {
  const [exp, setExp] = useState([]);
  useEffect(() => { api.get("/guides/bank/consent").then((r) => setExp(r.data.expiring)).catch(() => {}); }, []);
  if (!exp.length) return null;
  const renew = async (cid) => { try { const r = await api.post(`/powens/connections/${cid}/reconnect`); window.location.assign(r.data.url); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } };
  return (
    <div className="flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-2xl px-5 py-4" data-testid="bank-consent-banner">
      <p className="flex-1 text-sm"><b>Votre autorisation bancaire expire le {exp[0].expires_at}.</b> Renouvelez-la pour garder le rapprochement automatique.</p>
      <button onClick={() => renew(exp[0].connection_id)} className="px-4 py-2 rounded-full bg-brand-greenDeep text-white text-sm font-semibold" data-testid="bank-consent-renew">Renouveler</button>
    </div>
  );
};
